import { z } from 'zod';

import { isReservedLogin } from './reserved-logins';

/**
 * Contrato da identity-api (`/api/identity/...`): a conta central de todos os
 * modulos. So credenciais e papeis de staff (RT-G1).
 */

// ---------------------------------------------------------------------------
// Campos
// ---------------------------------------------------------------------------

/** 3 a 32 caracteres; comeca por letra ou digito; depois letras, digitos, `.`, `_`, `-`. */
export const loginSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]{2,31}$/, 'login invalido');

/**
 * Um login novo (registo no site e jovens da Arena): o mesmo formato, mas sem
 * os nomes reservados (`RESERVED_LOGINS`). Os logins que ja existem nao mudam.
 */
export const newLoginSchema = loginSchema.refine(
  (login) => !isReservedLogin(login),
  'esse nome esta reservado: escolhe outro',
);

export const emailSchema = z.string().trim().toLowerCase().email().max(254);

/**
 * Minimo 12 caracteres, sem regras de composicao (NIST 800-63B). O maximo existe
 * para limitar o custo do argon2 a um pedido.
 */
export const passwordSchema = z.string().min(12, 'minimo 12 caracteres').max(128);

export const totpCodeSchema = z.string().regex(/^\d{6}$/, 'codigo de 6 digitos');

/** `xxxxx-xxxxx`, sem distincao de maiusculas. */
export const recoveryCodeSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9]{5}-?[a-z0-9]{5}$/, 'codigo de recuperacao invalido');

export const accountKindSchema = z.enum(['standard', 'youth']);
export type AccountKind = z.infer<typeof accountKindSchema>;

// ---------------------------------------------------------------------------
// Registo e login
// ---------------------------------------------------------------------------

export const registerRequestSchema = z.object({
  login: newLoginSchema,
  email: emailSchema,
  password: passwordSchema,
});
export type RegisterRequest = z.infer<typeof registerRequestSchema>;

export const loginRequestSchema = z.object({
  /** Login ou email. */
  identifier: z.string().trim().min(1).max(254),
  password: z.string().min(1).max(128),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const accountSummarySchema = z.object({
  id: z.string().uuid(),
  login: z.string(),
  email: z.string().nullable(),
  emailVerified: z.boolean(),
  kind: accountKindSchema,
  /**
   * Ja escolheu password. `false` numa conta importada que ainda nao foi
   * ativada (docs/importacao 3.5) e sempre num jovem da Arena. Opcional para
   * nao obrigar os duplos de teste dos outros modulos a mudar.
   */
  hasPassword: z.boolean().optional(),
  /** Quando a conta foi criada (ISO). Opcional pelo mesmo motivo. */
  createdAt: z.string().optional(),
});
export type AccountSummary = z.infer<typeof accountSummarySchema>;

export const authenticatedResponseSchema = z.object({
  status: z.literal('authenticated'),
  /** JWT ES256, curto (15 min). Vai no header `Authorization: Bearer`. */
  accessToken: z.string(),
  accessTokenExpiresAt: z.string().datetime(),
  account: accountSummarySchema,
});
export type AuthenticatedResponse = z.infer<typeof authenticatedResponseSchema>;

export const mfaMethodSchema = z.enum(['totp', 'passkey', 'recovery_code']);
export type MfaMethod = z.infer<typeof mfaMethodSchema>;

/**
 * Resposta do `POST /sessions` (login com password):
 *
 * - `authenticated`: login completo; o refresh token vai num cookie httpOnly.
 * - `mfa_required`: a conta tem segundo fator; repetir com o `challengeToken`
 *   num dos endpoints `/sessions/mfa/...`.
 * - `mfa_enrollment_required`: a conta e de staff e ainda nao tem segundo fator
 *   (obrigatorio, RT-G4). O `challengeToken` so serve para o configurar.
 */
export const loginResponseSchema = z.discriminatedUnion('status', [
  authenticatedResponseSchema,
  z.object({
    status: z.literal('mfa_required'),
    challengeToken: z.string(),
    methods: z.array(mfaMethodSchema),
  }),
  z.object({
    status: z.literal('mfa_enrollment_required'),
    challengeToken: z.string(),
  }),
]);
export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const mfaTotpRequestSchema = z.object({
  challengeToken: z.string().min(1),
  code: totpCodeSchema,
});

export const mfaRecoveryRequestSchema = z.object({
  challengeToken: z.string().min(1),
  code: recoveryCodeSchema,
});

export const challengeTokenRequestSchema = z.object({
  challengeToken: z.string().min(1),
});

// ---------------------------------------------------------------------------
// Configurar o segundo fator
// ---------------------------------------------------------------------------

export const totpSetupResponseSchema = z.object({
  /** Base32, para quem escreve a chave a mao. */
  secret: z.string(),
  /** `otpauth://totp/...`, para o QR code. */
  otpauthUrl: z.string(),
});
export type TotpSetupResponse = z.infer<typeof totpSetupResponseSchema>;

/**
 * Pedidos de configuracao do segundo fator. Com sessao normal vai o token de
 * acesso no header; no login de staff sem segundo fator (`mfa_enrollment_required`)
 * vai antes o `challengeToken` no corpo.
 */
export const enrollmentRequestSchema = z.object({
  challengeToken: z.string().min(1).optional(),
});

export const totpConfirmRequestSchema = enrollmentRequestSchema.extend({ code: totpCodeSchema });

export const recoveryCodesResponseSchema = z.object({
  /** Mostrados uma unica vez; so o hash fica guardado. */
  recoveryCodes: z.array(z.string()),
});
export type RecoveryCodesResponse = z.infer<typeof recoveryCodesResponseSchema>;

/**
 * Fim de uma configuracao de segundo fator. Se veio de um login de staff
 * (`challengeToken`), traz a sessao: o login fica completo.
 */
export const enrollmentResultSchema = z.object({
  recoveryCodes: z.array(z.string()).nullable(),
  session: authenticatedResponseSchema.nullable(),
});
export type EnrollmentResult = z.infer<typeof enrollmentResultSchema>;

/**
 * WebAuthn: as opcoes e as respostas sao as do `@simplewebauthn/browser`, que o
 * front-end passa de um lado para o outro sem as abrir. Validadas a fundo no
 * servidor pelo `@simplewebauthn/server`. Cada cerimonia tem um `ceremonyId` de
 * uso unico.
 */
export const webauthnPayloadSchema = z.record(z.unknown());

export const passkeyOptionsResponseSchema = z.object({
  ceremonyId: z.string().uuid(),
  options: webauthnPayloadSchema,
});
export type PasskeyOptionsResponse = z.infer<typeof passkeyOptionsResponseSchema>;

/** Login so com passkey (sem password): conta como segundo fator. */
export const passkeyLoginVerifyRequestSchema = z.object({
  ceremonyId: z.string().uuid(),
  response: webauthnPayloadSchema,
});

/** Passkey como segundo passo depois da password. */
export const passkeyMfaVerifyRequestSchema = passkeyLoginVerifyRequestSchema.extend({
  challengeToken: z.string().min(1),
});

export const passkeyRegisterVerifyRequestSchema = passkeyLoginVerifyRequestSchema.extend({
  challengeToken: z.string().min(1).optional(),
  name: z.string().trim().min(1).max(60).optional(),
});

// ---------------------------------------------------------------------------
// Conta autenticada
// ---------------------------------------------------------------------------

export const staffAssignmentSchema = z.object({
  role: z.string(),
  roleName: z.string(),
  /** `null` = todas as arenas. */
  arenaId: z.string().uuid().nullable(),
});
export type StaffAssignment = z.infer<typeof staffAssignmentSchema>;

export const meResponseSchema = z.object({
  account: accountSummarySchema,
  staff: z.object({
    assignments: z.array(staffAssignmentSchema),
    grants: z.array(z.string()),
  }),
  mfa: z.object({
    totp: z.boolean(),
    passkeys: z.array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        createdAt: z.string().datetime(),
        lastUsedAt: z.string().datetime().nullable(),
      }),
    ),
    recoveryCodesLeft: z.number().int().nonnegative(),
    /** Staff sem segundo fator nao consegue entrar; isto nunca e `true` numa sessao valida de staff. */
    required: z.boolean(),
  }),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

export const changePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(128),
  newPassword: passwordSchema,
});

export const passwordResetRequestSchema = z.object({ email: emailSchema });
export const passwordResetConfirmSchema = z.object({
  token: z.string().min(1),
  newPassword: passwordSchema,
});
export const verifyEmailRequestSchema = z.object({ token: z.string().min(1) });
/** Ativar uma conta importada, sem password, com o link do email de boas-vindas. */
export const claimAccountRequestSchema = passwordResetConfirmSchema;

// ---------------------------------------------------------------------------
// Gestao de staff (permissao `staff.manage`)
// ---------------------------------------------------------------------------

export const roleSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  description: z.string(),
  permissions: z.array(z.string()),
});
export type Role = z.infer<typeof roleSchema>;

export const createRoleRequestSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,40}$/),
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).default(''),
  permissions: z.array(z.string()).min(1),
});

export const updateRoleRequestSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  description: z.string().trim().max(500).optional(),
  permissions: z.array(z.string()).min(1).optional(),
});

export const assignRoleRequestSchema = z.object({
  accountId: z.string().uuid(),
  role: z.string(),
  arenaId: z.string().uuid().nullable().default(null),
});

/** A lista final de papeis de uma pessoa: o que falta sai, o que e novo entra, tudo junto. */
export const setStaffRolesRequestSchema = z.object({
  assignments: z
    .array(
      z.object({
        role: z.string(),
        /** `null` = todas as arenas (ou papel sem arena). */
        arenaId: z.string().uuid().nullable().default(null),
      }),
    )
    .max(100),
});
export type SetStaffRolesRequest = z.infer<typeof setStaffRolesRequestSchema>;

export const staffMemberSchema = z.object({
  account: accountSummarySchema,
  assignments: z.array(staffAssignmentSchema.extend({ id: z.string().uuid() })),
  mfaConfigured: z.boolean(),
});
export type StaffMember = z.infer<typeof staffMemberSchema>;

// ---------------------------------------------------------------------------
// Token de acesso
// ---------------------------------------------------------------------------

export const ACCESS_TOKEN_ISSUER = 'https://growup.gg/identity';
export const ACCESS_TOKEN_AUDIENCE = 'growup';

/**
 * Claims do JWT de acesso. Qualquer API valida-o com a chave publica da
 * identity-api (JWKS em `/internal/jwks.json`); ver `@growup/auth`.
 */
export const accessTokenClaimsSchema = z.object({
  iss: z.literal(ACCESS_TOKEN_ISSUER),
  aud: z.literal(ACCESS_TOKEN_AUDIENCE),
  /** account_id. */
  sub: z.string().uuid(),
  login: z.string(),
  kind: accountKindSchema,
  /** Este login passou por um segundo fator. */
  mfa: z.boolean(),
  /** Concessoes de staff: `"<permissao>"` ou `"<permissao>@<arenaId>"`. Vazio para nao-staff. */
  grants: z.array(z.string()),
  iat: z.number(),
  exp: z.number(),
  jti: z.string(),
});
export type AccessTokenClaims = z.infer<typeof accessTokenClaimsSchema>;

// ---------------------------------------------------------------------------
// Interno (so na rede interna; header `X-Internal-Token`)
// ---------------------------------------------------------------------------

/** `POST /internal/accounts`: a members-api cria a conta de um jovem da Arena. */
export const internalCreateYouthAccountRequestSchema = z.object({
  login: loginSchema,
});

export const internalAccountsLookupRequestSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(500),
});

export const internalAccountsLookupResponseSchema = z.object({
  accounts: z.array(accountSummarySchema),
});

/**
 * Pesquisa de contas para as sugestoes dos campos de pesquisa do back-office:
 * login ou email que contenham `q` (minimo 2 letras), os que comecam por `q`
 * primeiro. Poucas linhas: e para escolher uma conta, nao para listar.
 */
export const accountSearchQuerySchema = z.object({
  q: z.string().trim().min(2).max(100),
  limit: z.coerce.number().int().min(1).max(20).default(8),
});
export type AccountSearchQuery = z.infer<typeof accountSearchQuerySchema>;

/** `POST /internal/accounts/search`: a site-api, para as sugestoes da venda ao balcao. */
export const internalAccountSearchRequestSchema = accountSearchQuerySchema.extend({
  kind: accountKindSchema.optional(),
});

export const internalAccountSearchResponseSchema = z.object({
  accounts: z.array(accountSummarySchema),
});

/** `POST /internal/accounts/find`: a site-api procura o cliente de uma venda ao balcao. 404 se nao existir. */
export const internalFindAccountRequestSchema = z.object({
  identifier: z.string().trim().min(1).max(254),
});

/**
 * `POST /internal/accounts/import`: a members-api cria uma conta vinda da base
 * antiga, sem password (as antigas eram SHA-1 e nao se migram). Idempotente
 * pelo `id`, escolhido pela members-api: repetir devolve a mesma conta.
 * Responde 409 com `field: 'login'` ou `field: 'email'` se ja estiverem usados
 * por outra conta.
 */
export const internalImportAccountRequestSchema = z.object({
  id: z.string().uuid(),
  login: loginSchema,
  email: emailSchema.nullable(),
  kind: accountKindSchema,
  createdAt: z.string().datetime(),
});
export type InternalImportAccountRequest = z.infer<typeof internalImportAccountRequestSchema>;

/**
 * `POST /internal/accounts/:id/claim-invite`: email de boas-vindas com o link
 * para escolher a password (14 dias). `import`: socio da base antiga, no
 * lancamento; `adult`: jovem da Arena que fez 18 anos e passa a ter conta do
 * site. `missing` sao as frases do que falta no perfil, se faltar alguma coisa.
 * 409 se a conta ja tiver password; 400 se nao tiver email.
 */
export const internalClaimInviteRequestSchema = z.object({
  reason: z.enum(['import', 'adult']).default('import'),
  missing: z.array(z.string().trim().min(1).max(120)).max(15).default([]),
});
export type InternalClaimInviteRequest = z.input<typeof internalClaimInviteRequestSchema>;

/**
 * `POST /internal/accounts/:id/promote`: a conta de um jovem da Arena que fez
 * 18 anos passa a conta do site, com o mesmo login e este email (por
 * confirmar ate a pessoa abrir o link de ativacao). 409 com `field: 'email'` se
 * o email for de outra conta; 409 sem campo se a conta ja nao for de jovem.
 */
export const internalPromoteAccountRequestSchema = z.object({
  email: emailSchema,
});
